import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Shield,
  Loader
} from 'lucide-react';
import securityApi from '../../api/securityApi';
import toast from 'react-hot-toast';

// Dashboard components
import EvidenceQualityBar from '../../components/compliance/dashboard/EvidenceQualityBar';
import DaysToTargetCard from '../../components/compliance/dashboard/DaysToTargetCard';
import TSCCategoryProgress from '../../components/compliance/dashboard/TSCCategoryProgress';
import AutomationCoverageChart from '../../components/compliance/dashboard/AutomationCoverageChart';

// Empty state components
import NoIntegrationsPrompt from '../../components/compliance/empty-states/NoIntegrationsPrompt';
import AuditReadyCelebration from '../../components/compliance/empty-states/AuditReadyCelebration';

// Task list components
import TaskList from '../../components/compliance/tasks/TaskList';
import TaskDetailPanel from '../../components/compliance/tasks/TaskDetailPanel';

// Export modal
import ExportConfirmationModal from '../../components/compliance/ExportConfirmationModal';

const ComplianceProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState('overview'); // overview | tasks
  const [project, setProject] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Task pagination and filters
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    priority: null,
    category: null,
    integrationSource: null,
    evidenceTier: null,
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'priority',
    sortOrder: 'desc',
  });

  // Memoized fetch tasks function to avoid stale closures
  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const response = await securityApi.getProjectTasks(projectId, {
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sortConfig.sortBy,
        sortOrder: sortConfig.sortOrder,
        status: filters.status,
        priority: filters.priority,
        category: filters.category,
        integrationSource: filters.integrationSource,
        evidenceTier: filters.evidenceTier,
      });

      setTasks(response.tasks || []);
      setPagination((prev) => ({
        ...prev,
        page: response.page || 1,
        pageSize: response.page_size || 25,
        total: response.total || 0,
        totalPages: response.total_pages || 0,
      }));
    } catch (err) {
      console.error('Error fetching tasks:', err);
      toast.error('Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  }, [projectId, pagination.page, pagination.pageSize, sortConfig.sortBy, sortConfig.sortOrder, filters]);

  // Fetch project and dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [projectData, dashData] = await Promise.all([
          securityApi.getComplianceProject(projectId),
          securityApi.getDashboard(projectId),
        ]);
        setProject(projectData);
        setDashboardData(dashData);
      } catch (err) {
        console.error('Error fetching project data:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  // Fetch tasks when switching to tasks tab or when filters/pagination change
  useEffect(() => {
    if (activeTab === 'tasks') {
      fetchTasks();
    }
  }, [activeTab, fetchTasks]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const dashData = await securityApi.getDashboard(projectId);
      setDashboardData(dashData);
      if (activeTab === 'tasks') {
        await fetchTasks();
      }
      toast.success('Data refreshed');
    } catch (err) {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  // Task list handlers
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setSortConfig({ sortBy, sortOrder });
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1, // Reset to first page when changing page size
    }));
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskPanelUpdated = async () => {
    await fetchTasks();
    try {
      const dashData = await securityApi.getDashboard(projectId);
      setDashboardData(dashData);
    } catch (err) {
      console.error('Error refreshing dashboard:', err);
    }
  };

  const handleTaskUpdate = async (taskId, updates) => {
    try {
      await securityApi.updateProjectTask(projectId, taskId, updates);
      toast.success('Task updated successfully');
      // Refresh tasks and dashboard
      await fetchTasks();
      const dashData = await securityApi.getDashboard(projectId);
      setDashboardData(dashData);
    } catch (err) {
      console.error('Error updating task:', err);
      throw err; // Re-throw to let the component handle it
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Project</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/compliance/projects')}
            className="text-red-700 hover:text-red-800 font-medium"
          >
            Back to Compliance
          </button>
        </div>
      </div>
    );
  }

  const {
    progress_percentage = 0,
    days_to_target = null,
    target_date = null,
    tasks_by_status = {},
    by_tsc_category = {},
    high_priority_remaining = [],
    audit_ready = false,
    connected_integrations = [],
  } = dashboardData || {};

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/compliance/projects')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {project?.name || 'Compliance Project'}
            </h1>
            <p className="text-sm text-gray-500">
              {project?.framework_name || 'SOC 2 Type I'} Compliance
            </p>
          </div>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'overview'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'tasks'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Tasks
        </button>
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <>
          {/* Audit Ready Celebration */}
          {audit_ready && (
            <AuditReadyCelebration
              onGenerateReport={() => toast('Report generation coming soon!')}
              onExportPackage={() => setShowExportModal(true)}
            />
          )}

          {/* Row 1: Progress Ring + Days to Target + Status Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <EvidenceQualityBar
                breakdown={dashboardData?.evidence_quality_breakdown}
                progressPercentage={dashboardData?.progress_percentage}
              />

            {days_to_target !== null && (
              <DaysToTargetCard days={days_to_target} targetDate={target_date} />
            )}

            {/* Status Breakdown */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Status Breakdown</h3>
              <div className="space-y-3">
                <StatusCard
                  label="Auto Verified"
                  count={tasks_by_status.auto_verified || 0}
                  icon={<CheckCircle className="w-4 h-4 text-green-500" />}
                  bgColor="bg-green-50"
                  textColor="text-green-700"
                />
                <StatusCard
                  label="Manually Verified"
                  count={tasks_by_status.manually_verified || 0}
                  icon={<CheckCircle className="w-4 h-4 text-blue-500" />}
                  bgColor="bg-blue-50"
                  textColor="text-blue-700"
                />
                <StatusCard
                  label="Pending"
                  count={tasks_by_status.pending || 0}
                  icon={<Clock className="w-4 h-4 text-amber-500" />}
                  bgColor="bg-amber-50"
                  textColor="text-amber-700"
                />
                <StatusCard
                  label="Failed"
                  count={tasks_by_status.failed || 0}
                  icon={<AlertCircle className="w-4 h-4 text-red-500" />}
                  bgColor="bg-red-50"
                  textColor="text-red-700"
                />
              </div>
            </div>
          </div>

          {/* Row 2: TSC Category Progress */}
          <div className="mb-6">
            <TSCCategoryProgress data={by_tsc_category} />
          </div>

          {/* Row 3: Automation Coverage Chart */}
          <div className="mb-6">
            <AutomationCoverageChart
              automationCoverage={dashboardData?.automation_coverage}
              projectId={projectId}
            />
          </div>

          {/* Row 4: High Priority Tasks Preview */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                High Priority Tasks
              </h3>
              <span className="text-sm text-gray-500">
                {high_priority_remaining.length} remaining
              </span>
            </div>

            {high_priority_remaining.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>All high priority tasks completed!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {high_priority_remaining.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono bg-gray-200 px-2 py-0.5 rounded">
                          {task.control_id}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            task.priority === 'critical'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {task.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 truncate">{task.title}</p>
                    </div>
                    {task.auto_checkable && (
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded ml-2 flex-shrink-0">
                        Auto
                      </span>
                    )}
                  </div>
                ))}
                {high_priority_remaining.length > 5 && (
                  <button
                    onClick={() => setActiveTab('tasks')}
                    className="w-full py-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
                  >
                    View all {high_priority_remaining.length} high priority tasks
                  </button>
                )}
              </div>
            )}
          </div>

          {/* No Integrations Prompt */}
          {!connected_integrations || connected_integrations.length === 0 ? (
            <NoIntegrationsPrompt projectId={projectId} />
          ) : null}

          {/* Last Scan Info */}
          {dashboardData?.last_scan_at && (
            <div className="mt-6 text-center text-sm text-gray-500">
              Last scan: {new Date(dashboardData.last_scan_at).toLocaleString()}
            </div>
          )}
        </>
      )}

      {activeTab === 'tasks' && (
        <TaskList
          tasks={tasks}
          pagination={pagination}
          filters={filters}
          sortConfig={sortConfig}
          loading={tasksLoading}
          error={null}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onTaskUpdate={handleTaskUpdate}
          onTaskClick={handleTaskClick}
        />
      )}

      {/* Task Detail Slide-over Panel */}
      {selectedTask && (
        <TaskDetailPanel
          task={selectedTask}
          projectId={projectId}
          onClose={() => setSelectedTask(null)}
          onTaskUpdated={handleTaskPanelUpdated}
        />
      )}

      {/* Export Confirmation Modal */}
      {showExportModal && (
        <ExportConfirmationModal
          projectId={projectId}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

// Status Card Component (for compact display in overview)
const StatusCard = ({ label, count, icon, bgColor, textColor }) => (
  <div className={`${bgColor} rounded-lg p-3 flex items-center justify-between`}>
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-xs font-medium text-gray-700">{label}</span>
    </div>
    <span className={`text-lg font-bold ${textColor}`}>{count}</span>
  </div>
);

export default ComplianceProjectDetail;
